import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Link,
  FormHelperText,
  Switch,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Stack,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Fade,
  Zoom
} from '@mui/material';
import { 
  ArrowBack, 
  Visibility, 
  VisibilityOff, 
  CheckCircle, 
  VerifiedUser, 
  Security, 
  Savings,
  Facebook,
  Google,
  Apple
} from '@mui/icons-material';
import { sendEmailCodePreRegister, checkInviteCode } from '../../api/modules/auth';
import { showMessage } from '../../utils';
import TopBar from '../../components/TopBar';
import { removeToken } from '../../utils/auth';
import { queryInviteInfoByCode } from '../../api/modules/payment';

// 更新主题色系
const PRIMARY = '#1677ff'; // 品牌主色
const SECONDARY = '#8f222e'; // 品牌副色
const WARNING = '#fa8c16'; // 警告色
const SUCCESS = '#52c41a'; // 成功色
const DARK_BG = '#040227'; // 深色背景
const DARK_TEXT = '#ffffff'; // 深色背景下的文本
const MEDIUM_TEXT = '#a3a3a3'; // 中等文本
const LIGHT_TEXT = '#666666'; // 浅色文本
const INPUT_BG = 'rgba(30, 30, 60, 0.4)'; // 输入框背景色

// 玻璃态效果
const glassEffect = {
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
};

// 过渡动画
const transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1]
};

const SignUpInviteCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [username, setUsername] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // 从URL获取inviteCode参数
  const searchParams = new URLSearchParams(window.location.search);
  const urlInviteCode = searchParams.get('inviteCode') || '';

  // 表单数据
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    newsletter: true,
    terms: false,
    inviteCode: urlInviteCode
  });

  // 错误信息
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms: '',
    inviteCode: ''
  });

  // 密码强度检测
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  useEffect(() => {
    fetchInviteInfoByCode(urlInviteCode);
  }, [urlInviteCode]);
  
  const fetchInviteInfoByCode = (code) => {
    try {
    queryInviteInfoByCode({ inviteCode: code }, (rst) => {
        const { code, data, msg } = rst || {};
      if (code === 200 && data) {
        setUsername(data);
      } else {
          showMessage(msg || "Failed to fetch invite information", "error");
        }
      })
      .catch(error => {
        console.error("Network error when fetching invite info:", error);
        showMessage("Network error. Please try again later.", "error");
      });
    } catch (error) {
      console.error("Error in fetchInviteInfoByCode:", error);
      showMessage("An error occurred. Please try again later.", "error");
    }
  };

  // 处理密码变化
  const handlePasswordChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, password: value }));
    setPasswordStrength(checkPasswordStrength(value));
  };

  // 获取密码强度颜色
  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 1: return WARNING;
      case 2: return '#FF9500';
      case 3: return '#FFCC00';
      case 4: return SUCCESS;
      default: return '#E0E0E0';
    }
  };

  // 获取密码强度文本
  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Strong';
      default: return '';
    }
  };

  // 验证当前步骤的字段
  const validateCurrentStep = () => {
    const newErrors = {};
    let isValid = true;

    switch (currentStep) {
      case 1:
        // 验证第一步的字段
    if (!formData.firstName.trim()) {
          newErrors.firstName = 'Please enter first name';
      isValid = false;
    }
    if (!formData.lastName.trim()) {
          newErrors.lastName = 'Please enter last name';
      isValid = false;
    }
        if (!formData.phone.trim()) {
          newErrors.phone = 'Please enter phone number';
          isValid = false;
        } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
          newErrors.phone = 'Please enter a valid phone number';
      isValid = false;
    }
        if (!formData.email.trim()) {
          newErrors.email = 'Please enter email';
      isValid = false;
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
        break;

      case 2:
        // 验证第二步的字段
    if (!formData.password) {
          newErrors.password = 'Please enter password';
          isValid = false;
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }
    if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
        break;

      case 3:
        // 验证第三步的字段
        if (!formData.inviteCode.trim()) {
          newErrors.inviteCode = 'Please enter invitation code';
      isValid = false;
    }
        if (!formData.terms) {
          newErrors.terms = 'Please agree to the terms of service';
      isValid = false;
        }
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  // 处理下一步按钮点击
  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  // 处理上一步按钮点击
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // 渲染表单步骤
  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
  return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: DARK_TEXT }}>
                Step 1: Personal Information
                </Typography>
              <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                    <TextField
                      fullWidth
                  label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName}
                  variant="outlined"
                  InputProps={{
                    sx: {
                      backgroundColor: 'transparent',
                      '& input': {
                        backgroundColor: '#040227',
                        borderRadius: 3,
                        color: DARK_TEXT,
                        fontSize: '16px',
                        padding: '15px 20px',
                      },
                      '& input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                        WebkitTextFillColor: DARK_TEXT
                      },
                      '& input:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                        WebkitTextFillColor: DARK_TEXT
                      }
                    }
                  }}
                      sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: PRIMARY
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: MEDIUM_TEXT
                    },
                    '& .MuiInputBase-input': {
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px'
                    },
                    '& .MuiFormHelperText-root': {
                      color: MEDIUM_TEXT,
                      marginLeft: 0
                    }
                  }}
                />
                    <TextField
                      fullWidth
                  label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName}
                  variant="outlined"
                  InputProps={{
                    sx: {
                      backgroundColor: 'transparent',
                      '& input': {
                        backgroundColor: '#040227',
                        borderRadius: 3,
                        color: DARK_TEXT,
                        fontSize: '16px',
                        padding: '15px 20px',
                      },
                      '& input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                        WebkitTextFillColor: DARK_TEXT
                      },
                      '& input:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                        WebkitTextFillColor: DARK_TEXT
                      }
                    }
                  }}
                      sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: PRIMARY
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: MEDIUM_TEXT
                    },
                    '& .MuiInputBase-input': {
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px'
                    },
                    '& .MuiFormHelperText-root': {
                      color: MEDIUM_TEXT,
                      marginLeft: 0
                    }
                      }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                variant="outlined"
                InputProps={{
                  sx: {
                    backgroundColor: 'transparent',
                    '& input': {
                      backgroundColor: '#040227',
                      borderRadius: 3,
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    }
                  }
                }}
                    sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: PRIMARY
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: MEDIUM_TEXT
                  },
                  '& .MuiInputBase-input': {
                    color: DARK_TEXT,
                    fontSize: '16px',
                    padding: '15px 20px'
                  },
                  '& .MuiFormHelperText-root': {
                    color: MEDIUM_TEXT,
                    marginLeft: 0
                  }
                }}
              />
                  <TextField
                    fullWidth
                label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                variant="outlined"
                InputProps={{
                  sx: {
                    backgroundColor: 'transparent',
                    '& input': {
                      backgroundColor: '#040227',
                      borderRadius: 3,
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    }
                  }
                }}
                    sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: PRIMARY
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: MEDIUM_TEXT
                  },
                  '& .MuiInputBase-input': {
                    color: DARK_TEXT,
                    fontSize: '16px',
                    padding: '15px 20px'
                  },
                  '& .MuiFormHelperText-root': {
                    color: MEDIUM_TEXT,
                    marginLeft: 0
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleNextStep}
                sx={{
                  mt: 2,
                  bgcolor: PRIMARY,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  height: 52,
                  boxShadow: `0 4px 12px ${PRIMARY}30`,
                  '&:hover': { bgcolor: '#0958d9' }
                }}
              >
                Continue
              </Button>
                </Box>
          </Fade>
        );
      case 2:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: DARK_TEXT }}>
                Step 2: Security Setup
                  </Typography>
                  <TextField
                    fullWidth
                label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                onChange={handlePasswordChange}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                variant="outlined"
                    InputProps={{
                      endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      ),
                  sx: {
                    backgroundColor: 'transparent',
                    '& input': {
                      backgroundColor: '#040227',
                      borderRadius: 3,
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    }
                  }
                    }}
                    sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: '#040227',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: PRIMARY
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: MEDIUM_TEXT
                  },
                  '& .MuiFormHelperText-root': {
                    color: MEDIUM_TEXT,
                    marginLeft: 0
                  }
                }}
              />
              {formData.password && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(passwordStrength / 4) * 100}
                    sx={{ 
                      height: 4, 
                      borderRadius: 2,
                      backgroundColor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPasswordStrengthColor()
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: getPasswordStrengthColor(), mt: 0.5 }}>
                    Password Strength: {getPasswordStrengthText()}
                  </Typography>
                </Box>
              )}
                  <TextField
                    fullWidth
                label="Confirm Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword}
                variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      ),
                  sx: {
                    backgroundColor: 'transparent',
                    '& input': {
                      backgroundColor: '#040227',
                      borderRadius: 3,
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    }
                  }
                    }}
                    sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: '#040227',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: PRIMARY
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: MEDIUM_TEXT
                  },
                  '& .MuiFormHelperText-root': {
                    color: MEDIUM_TEXT,
                    marginLeft: 0
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handlePrevStep}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 16,
                    height: 52,
                    borderColor: PRIMARY,
                    color: PRIMARY,
                    '&:hover': { borderColor: '#0958d9', color: '#0958d9' }
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNextStep}
                  sx={{
                    bgcolor: PRIMARY,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 16,
                    height: 52,
                    boxShadow: `0 4px 12px ${PRIMARY}30`,
                    '&:hover': { bgcolor: '#0958d9' }
                  }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Fade>
        );
      case 3:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: DARK_TEXT }}>
                Step 3: Final Details
              </Typography>
              <TextField
                fullWidth
                label="Invitation Code"
                name="inviteCode"
                value={formData.inviteCode}
                    onChange={handleChange}
                error={Boolean(errors.inviteCode)}
                helperText={errors.inviteCode}
                variant="outlined"
                InputProps={{
                  sx: {
                    backgroundColor: 'transparent',
                    '& input': {
                      backgroundColor: '#040227',
                      borderRadius: 3,
                      color: DARK_TEXT,
                      fontSize: '16px',
                      padding: '15px 20px',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #040227 inset',
                      WebkitTextFillColor: DARK_TEXT
                    }
                  }
                }}
                    sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: PRIMARY
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: MEDIUM_TEXT
                  },
                  '& .MuiInputBase-input': {
                    color: DARK_TEXT,
                    fontSize: '16px',
                    padding: '15px 20px'
                  },
                  '& .MuiFormHelperText-root': {
                    color: MEDIUM_TEXT,
                    marginLeft: 0
                  }
                }}
              />
              <Box display="flex" alignItems="center" mb={2}>
                <Switch
                  checked={formData.newsletter}
                  onChange={e => setFormData(prev => ({ ...prev, newsletter: e.target.checked }))}
                  name="newsletter"
                    sx={{
                    mr: 1,
                    '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY }
                    }}
                />
                <Typography sx={{ fontSize: 14, color: DARK_TEXT }}>
                    Subscribe to our newsletters
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                  alignItems: "flex-start",
                  mb: 3,
                  pl: 0  // 移除左内边距
                  }}
                >
                  <Checkbox
                    name="terms"
                    checked={formData.terms}
                    onChange={handleChange}
                    sx={{
                    padding: '0',  // 移除内边距
                    marginRight: '8px',  // 添加右边距
                    color: errors.terms ? '#ff4d4f' : PRIMARY,
                      "&.Mui-checked": {
                      color: errors.terms ? '#ff4d4f' : PRIMARY,
                      },
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                      fontSize: 14,
                      color: MEDIUM_TEXT,
                      lineHeight: 1.5,
                    }}
                  >
                    I have read and agree to the:{' '}
                      <Link
                        href="https://utaxes.com/help/policies/terms-of-service"
                        sx={{
                        color: PRIMARY,
                          textDecoration: "none",
                          mx: "2px",
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                        }}
                      >
                        Terms of Service
                      </Link>
                    {' '}and{' '}
                      <Link
                        href="https://utaxes.com/help/policies/privacy-policy"
                        sx={{
                        color: PRIMARY,
                          textDecoration: "none",
                          ml: "2px",
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                        }}
                      >
                        Privacy Policy
                      </Link>
                    </Typography>
                    {errors.terms && (
                    <FormHelperText 
                      sx={{
                        color: MEDIUM_TEXT,
                        marginLeft: 0,
                        marginTop: '4px'
                      }}
                    >
                      {errors.terms}
                    </FormHelperText>
                    )}
                  </Box>
                </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handlePrevStep}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 16,
                    height: 52,
                    borderColor: PRIMARY,
                    color: PRIMARY,
                    '&:hover': { borderColor: '#0958d9', color: '#0958d9' }
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: PRIMARY,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 16,
                    height: 52,
                    boxShadow: `0 4px 12px ${PRIMARY}30`,
                    '&:hover': { bgcolor: '#0958d9' }
                  }}
                >
                  Sign Up Now
                </Button>
                </Box>
              </Box>
          </Fade>
        );
      default:
        return null;
    }
  };

  // 处理输入变化
  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'newsletter' || name === 'terms' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 处理弹框关闭
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // 处理强制继续注册
  const handleForceContinue = async () => {
    setOpenDialog(false);
    await handleEmailCodeSend();
  };

  const handleEmailCodeSend = async () => {
    try {
      const registerData = {
        userType: '01',
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        subscribeFlag: formData.newsletter ? '1' : '0',
        email: formData.email,
        password: formData.password,
        repeatPassword: formData.confirmPassword,
        inviteType: 4,
        inviteCode: formData.inviteCode
      };
      
      showMessage('Sending verification code...', 'info');
      
      await sendEmailCodePreRegister(registerData, (rst) => {
        let { code, data, msg } = rst || {};
        if (code == 200) {
          showMessage('Verification code sent successfully', 'success');
          setTimeout(() => {
            navigate('/campaigns/invitations/verification?v=isInvitedUser', { 
              state: { 
                email: formData.email,
                registerData,
                from: location
              }
            });
          }, 500);
        } else {
          showMessage(msg || "Failed to send verification code", 'error');
        }
      })
      .catch(error => {
        console.error("Network error when sending email code:", error);
        showMessage("Network error. Please try again later.", "error");
      });
      
    } catch (error) {
      console.error("Error in handleEmailCodeSend:", error);
      showMessage(error.message || "An error occurred. Please try again later.", 'error');
    }
  };

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  
  // 表单验证函数
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // 验证名字
    if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
        isValid = false;
    }

    // 验证姓氏
    if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
        isValid = false;
    }

    // 验证电话
    if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
        isValid = false;
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
        isValid = false;
    }

    // 验证邮箱
    if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
        isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        isValid = false;
    }

    // 验证密码
    if (!formData.password) {
        newErrors.password = 'Password is required';
        isValid = false;
    } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
        isValid = false;
    }

    // 验证确认密码
    if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
        isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        isValid = false;
    }

    // 验证邀请码
    if (!formData.inviteCode.trim()) {
        newErrors.inviteCode = 'Invitation code is required';
        isValid = false;
    }

    // 验证条款同意
    if (!formData.terms) {
        newErrors.terms = 'You must agree to the terms and conditions';
        isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // 处理下载应用
  const handleDownload = () => {
    window.open('https://apps.apple.com/us/app/utaxes/id1234567890', '_blank');
  };
  
  // 获取主题和媒体查询
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 修改handleSubmit函数
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    console.log("Submitting form with data:", formData); // 添加日志
    if (validateForm()) {
        console.log("Form is valid, checking invite code..."); // 添加日志
        checkInviteCode({inviteCode: formData.inviteCode}, (rst) => {
            console.log("Invite code check response:", rst); // 添加日志
            let { code, data, msg } = rst;
            if (data) {
              handleEmailCodeSend();
            } else {
              setOpenDialog(true);
            }
        });
    } else {
        console.log("Form validation failed:", errors); // 添加日志
    }
  };

  return (
    <>
      <TopBar
        hideActions
        isLogin={false}
        onLogin={() => navigate('/campaigns/invitations/login?v=isInvitedUser')}
        onShareClick={() => navigate('/campaigns/invitations/referral')}
        onCouponClick={() => navigate('/campaigns/invitations/tax-declaration')}
        onAvatarClick={handleUserMenuOpen}
        userMenuAnchor={userMenuAnchor}
        onUserMenuClose={handleUserMenuClose}
        navigate={navigate}
        onLogout={() => {
          removeToken();
          showMessage("Logout successfully", "success");
          setTimeout(() => {
            navigate("/login?v=isInvitedUser");
          }, 1500);
          handleUserMenuClose();
        }}
      />
        <Box
          sx={{
          background: '#040227', // 使用品牌色的淡色渐变
          width: '100%',
          pt: '80px',
          pb: '80px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            backgroundColor: '#040227',
            flex: 1,
            pb: '80px',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '3px'
            }
          }}
        >
          {/* 页面标题 */}
          <Box sx={{ 
            textAlign: 'center', 
            mb: { xs: 4, md: 6 },
            mt: { xs: 2, md: 4 }
          }}>
          <Typography
              variant="h4" 
            sx={{
                fontWeight: 800, 
                fontSize: { xs: '28px', md: '36px' },
                color: DARK_TEXT,
                mb: 1
              }}
            >
              Exclusive Invitation
          </Typography>
          <Typography
              variant="body1" 
            sx={{
                color: MEDIUM_TEXT,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '14px', md: '16px' }
              }}
            >
              From <Box component="span" sx={{ fontWeight: 600, color: PRIMARY }}>{username || 'uTaxes'}</Box> - Sign up now for exclusive tax service benefits
          </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'stretch' },
              gap: { xs: 4, md: 6 },
              mx: 'auto',
            }}
          >
            {/* 左侧内容区 */}
            <Box 
                sx={{
                flex: '1 1 45%',
                maxWidth: { xs: '100%', md: '500px' },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              {/* 邀请人卡片 */}
              <Paper 
                elevation={0} 
                  sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 4,
                  ...glassEffect,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      height: '120px',
                      background: `linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%)`,
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      border: '4px solid rgba(255, 255, 255, 0.2)',
                      position: 'absolute',
                      bottom: '-40px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: PRIMARY,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                    }}
                  >
                    {username ? username.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                </Box>
                
                <Box sx={{ pt: 6, pb: 3, px: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: DARK_TEXT }}>
                    {username || 'uTaxes'} invites you to join
                  </Typography>
                  <Typography variant="body2" sx={{ color: MEDIUM_TEXT, mb: 2 }}>
                    Get professional tax services and handle your tax matters with ease
                  </Typography>
                  
                    <Box
                      component="img"
                    src={require("../../assets/invite/logo-icon.png")}
                    alt="Logo"
                      sx={{
                      width: 60,
                      height: 'auto',
                      mx: 'auto',
                      display: 'block',
                      filter: 'brightness(0.9)'
                      }}
                    />
                  </Box>
              </Paper>

              {/* 特权列表 */}
              <Paper 
                elevation={0} 
                    sx={{
                  borderRadius: 3,
                  p: 3,
                  flex: 1,
                  ...glassEffect,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: DARK_TEXT }}>
                  Sign up for these exclusive benefits
                </Typography>
                
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar 
                        sx={{
                        bgcolor: 'rgba(22, 119, 255, 0.2)', 
                        color: PRIMARY, 
                        width: 36, 
                        height: 36, 
                        mr: 2 
                      }}
                    >
                      <Savings fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, color: DARK_TEXT }}>
                        Free uTaxes Pro Service
                      </Typography>
                      <Typography variant="body2" sx={{ color: MEDIUM_TEXT }}>
                        Enjoy a smarter, easier way to file your taxes
                      </Typography>
                  </Box>
                </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar 
                    sx={{
                        bgcolor: 'rgba(143, 34, 46, 0.2)', 
                        color: SECONDARY, 
                        width: 36, 
                        height: 36, 
                        mr: 2 
                      }}
                    >
                      <VerifiedUser fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, color: DARK_TEXT }}>
                        Professional Tax Advisor Support
                  </Typography>
                      <Typography variant="body2" sx={{ color: MEDIUM_TEXT }}>
                        Get personalized advice from tax experts
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'rgba(250, 140, 22, 0.2)', 
                        color: WARNING, 
                        width: 36, 
                        height: 36, 
                        mr: 2 
                      }}
                    >
                      <Security fontSize="small" />
                    </Avatar>
                  <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, color: DARK_TEXT }}>
                        Tax Refund Guarantee
                      </Typography>
                      <Typography variant="body2" sx={{ color: MEDIUM_TEXT }}>
                        Ensure you get the maximum tax refund amount
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Box>
            
            {/* 右侧表单区 */}
            <Box 
              sx={{ 
                flex: '1 1 55%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 0,
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  ...glassEffect,
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                    <Typography
                  variant="h5" 
                      sx={{
                    fontWeight: 700, 
                    fontSize: { xs: '22px', md: '24px' }, 
                    mb: 1, 
                    color: DARK_TEXT,
                    textAlign: 'center' 
                  }}
                >
                  Complete Registration for Your Exclusive Offer
                    </Typography>
                
                    <Typography
                      sx={{
                    color: MEDIUM_TEXT, 
                    fontSize: '14px', 
                    textAlign: 'center', 
                    mb: 4 
                  }}
                >
                  Fill in the information below to start enjoying professional tax services
                </Typography>

                {/* 进度指示器 */}
                <Box sx={{ mb: 4 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(currentStep / 3) * 100}
                    sx={{ 
                      height: 4, 
                      borderRadius: 2,
                      backgroundColor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: PRIMARY
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: MEDIUM_TEXT, mt: 0.5 }}>
                    Step {currentStep} of 3
                    </Typography>
                  </Box>
                
                {/* 注册表单 */}
                <Box 
                  component="form" 
                  onSubmit={handleSubmit}
                  sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    {renderFormStep()}
                </Box>
              </Box>

                {/* 登录链接 */}
                <Typography sx={{ color: MEDIUM_TEXT, fontSize: 13, textAlign: 'center', mt: 'auto', pt: 3 }}>
                  Already have an account?{' '}
                  <Link
                    onClick={() => navigate("/login?v=isInvitedUser",{state:{from:location}})}
                    sx={{
                      color: PRIMARY,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Log in
                  </Link>
                </Typography>
              </Paper>
          </Box>
        </Box>

          {/* 信任标志 */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: MEDIUM_TEXT, mb: 2 }}>
              Trusted Tax Services
            </Typography>
            <Box 
              sx={{
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 3, md: 6 },
                flexWrap: 'wrap'
              }}
            >
              <Box 
              sx={{
                  display: 'flex', 
                  alignItems: 'center', 
                  color: MEDIUM_TEXT,
                  fontSize: '14px'
                }}
              >
                <CheckCircle fontSize="small" sx={{ mr: 0.5, color: SUCCESS }} />
                Secure Encryption
              </Box>
              <Box 
              sx={{
                  display: 'flex', 
                  alignItems: 'center', 
                  color: MEDIUM_TEXT,
                  fontSize: '14px'
                }}
              >
                <CheckCircle fontSize="small" sx={{ mr: 0.5, color: SUCCESS }} />
                Professional Tax Advisors
              </Box>
              <Box 
              sx={{
                  display: 'flex', 
                  alignItems: 'center', 
                  color: MEDIUM_TEXT,
                  fontSize: '14px'
                }}
              >
                <CheckCircle fontSize="small" sx={{ mr: 0.5, color: SUCCESS }} />
                24/7 Customer Support
              </Box>
            </Box>
          </Box>
        </Container>
                  {/* 比例容器，保持 1200x628 的比例 */}
        <Box
          sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              '&:before': {
                content: '""',
                display: 'block',
                paddingTop: '66.6666%',
              },
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/banner-4.jpg`}
              alt="Header background"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #EAECF0",
            backgroundColor: "#080808",
            height: { xs: "60px", md: "80px" },
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 2, md: 6 },
              maxWidth: 'lg',
              mx: 'auto'
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={require("../../assets/images/logo-bottom.png")}
                alt="uTaxes"
                sx={{
                  width: 60,
                  height: 60,
                  marginRight: 1,
                }}
              />
              <Box sx={{ color: "white" }}>
                <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
                  uTaxes
                </Typography>
                <Typography sx={{ fontSize: "12px" }}>
                  Love, Peace of Mind 
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleDownload}
              sx={{
                backgroundColor: SECONDARY,
                color: "white",
                borderRadius: 5,
                fontSize: "13px",
                fontWeight: 500,
                padding: "10px 20px",
                "&:hover": {
                  backgroundColor: "#7a1d27", // 深色副色
                },
              }}
            >
              DOWNLOAD APP
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* 添加确认弹框 */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "16px",
          },
        }}
      >
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: DARK_TEXT,
              fontSize: "16px",
              textAlign: "center",
              mb: 2,
            }}
          >
            The invitation code is incorrect. Please check and try again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              width: "120px",
              color: MEDIUM_TEXT,
              borderColor: MEDIUM_TEXT,
              "&:hover": {
                borderColor: DARK_TEXT,
                color: DARK_TEXT,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForceContinue}
            variant="contained"
            sx={{
              borderRadius: "8px",
              width: "120px",
              bgcolor: PRIMARY,
              "&:hover": {
                bgcolor: "#0958d9",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignUpInviteCode;
